import WorkspacesService from "../infrastructure/Services/WorkspacesService";

import { UserType } from "../interfaces/User";

import { openDatabase } from "../Utils/db";

import { getScopedStreamOutputs } from "../Utils/streamOutputScope";

import { IProvider } from "./Streaming";

import CurrentUser from "./User";

import { IConfigDB } from "../interfaces/Config";

class CurrentWorkspace {
  // Perezoso a propósito: este objeto es un singleton de módulo y se construye al
  // evaluar el bundle, antes de que Root resuelva el puerto de la API local. Nada
  // que dependa del puerto se crea acá.
  private service: WorkspacesService | null = null;

  private get workspacesService(): WorkspacesService {
    if (!this.service) this.service = new WorkspacesService("api");

    return this.service;
  }

  async getPathWorkspace(): Promise<string | null> {
    const workspace = await this.workspacesService.getCurrent();

    return workspace ?? null;
  }

  async getProviders(): Promise<IProvider[]> {
    const db = await openDatabase();

    const streamOutput = await getScopedStreamOutputs(db);

    const providers = streamOutput.map((item) => ({
      endpoint: item.stream.streamUrl,

      key: item.stream.streamKey,

      name: item.stream?.config?.name || item.name,

      enabled: item.stream.enabled,

      // Ruteo cloud/local del destino (default local → Kero fan-out directo)

      routing: item.stream.routing ?? "local",

      // Override de calidad por destino (omitido cuando el destino usa el maestro)

      ...(item.stream.encoding ? { encoding: item.stream.encoding } : {}),
    }));

    return providers;
  }

  async getType(defaultType = UserType.Streamer): Promise<UserType> {
    // 1. Try local DB config first (fast)

    const config = await this.getConfig();

    const localType = config?.configuration?.userType as UserType | undefined;

    if (localType) return localType;

    // 2. Fallback: check the workspace API (server always has the correct type)

    try {
      const workspaces = await this.workspacesService.getAll();

      const current = workspaces?.find((ws) => ws.current);

      if (current?.userType) {
        return current.userType as UserType;
      }
    } catch (error) {
      // Antes se tragaba en silencio y el usuario recibía la escena del tipo por
      // defecto sin ninguna pista de por qué.
      console.warn(
        "[workspace] no se pudo consultar el tipo de usuario a la API local; se usa el default",
        error,
      );
    }

    return defaultType;
  }

  async getConfig(): Promise<IConfigDB | null> {
    const userId = CurrentUser.getUserId();

    if (!userId) {
      return null;
    }

    const db = await openDatabase();

    const configs = await db?.config.toArray();

    return configs?.find((item) => item.userId === userId) ?? null;
  }

  async getOutputDevice(): Promise<{ deviceId: string; label: string } | null> {
    const config = await this.getConfig();

    return config?.configuration?.outputDevice ?? null;
  }

  async getConfigValue<K extends keyof IConfigDB["configuration"]>(
    key: K,
  ): Promise<IConfigDB["configuration"][K] | undefined> {
    const config = await this.getConfig();

    return config?.configuration?.[key];
  }

  async getRootDirectory(): Promise<string | null> {
    const configValue = await this.getConfigValue("recordingPath");

    if (!configValue) return null;

    const segments = configValue.split(/[\\/]/);

    const lastSegment = segments[segments.length - 1];

    return lastSegment || null;
  }

  async getFolderPath(folderName: string): Promise<string | null> {
    const workspacePath = await this.getPathWorkspace();

    if (!workspacePath) {
      return null;
    }

    return `${workspacePath}\\${folderName}`;
  }

  async getRecordingPath(): Promise<string | null> {
    const userRecordingPath = await this.getConfigValue("recordingPath");

    if (userRecordingPath) {
      return userRecordingPath;
    }

    const userType = await this.getType();

    const folder = userType === "local_recording" ? "records" : "streaming";

    return this.getFolderPath(folder);
  }
}

export default new CurrentWorkspace();
